import React, { useEffect, useState } from "react";
import { Button, Select, Option, Chip, CircularProgress } from "@mui/joy";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../fetch/fetch";
import totalLocationData from "../../../../../data/locationdata";
import { englishLocationToKorean } from "../../../../../data/locationFuntion";

//지점별 인증 등록장치 설정 - 본사직원페이지 탭 (todoistApiKey 보유 계정만 저장 가능)
//여기서 저장한 값이 인증 지문/얼굴 등록 모달의 기본 장치가 됨 (로그인한 선생님 지점 기준)
//- 지문: 등록 방식(네트워크 장치 / USB 등록기) + 네트워크일 때 기본 장치
//- 얼굴: 촬영 장치 (비우면 기존 방식 = deviceInfos 의 입문/출문 장치)

const EXCLUDED_LOCATIONS = ["total", "songdo_free", "get50", "getL"];   // facerecognitionmodal.tsx 와 동일
const NONE = "__none";                                                    // Joy Select 에서 "선택 안 됨" 값
const API = "/dashboard/biostarenroll";

const toastOptions: any = { position: "bottom-right", autoClose: 3000, hideProgressBar: true, theme: "colored" };

interface SettingRow {
    location: string;
    fingerprintMode: "network" | "usb" | null;
    fingerprintDeviceId: string | null;
    fingerprintDeviceName: string | null;
    faceDeviceId: string | null;
    faceDeviceName: string | null;
    teacherName?: string | null;
    lastModified?: any;
}

const emptyRow = (location: string): SettingRow => ({
    location, fingerprintMode: null, fingerprintDeviceId: null, fingerprintDeviceName: null, faceDeviceId: null, faceDeviceName: null
});

const formatDateTime = (v: any) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return typeof v === "string" ? v : "";
    const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const BiostarEnrollDeviceSetting: React.FC<any> = (props) => {

    const branches = totalLocationData.filter((each) =>
        props.user && each.academy.includes(props.user.academy) && !EXCLUDED_LOCATIONS.includes(each.english));

    const [devices, setDevices] = useState<any[]>([]);
    const [fetchedAt, setFetchedAt] = useState<any>(null);
    const [legacyFaceDeviceIds, setLegacyFaceDeviceIds] = useState<any>({});
    const [settings, setSettings] = useState<{ [location: string]: SettingRow }>({});
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [now, setNow] = useState(Date.now());   // "M분 전 갱신" 표시용

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(timer);
    }, []);

    //장치 목록 + 지점별 설정을 같이 로드. 후보 지점 전부를 빈 행으로 깔고 서버 행을 덮어씀 (regularmealsettingmodal 패턴)
    const load = async () => {
        try {
            setLoading(true);

            const [deviceResult, settingResult]: any = await Promise.all([
                getFetch(`${API}/devices`),
                getFetch(`${API}/settings`)
            ]);

            if (deviceResult.message === "success") {
                setDevices(deviceResult.data || []);
                setFetchedAt(deviceResult.fetchedAt || null);
            } else {
                toast.error("BioStar 장치 목록을 불러오지 못했습니다", toastOptions);
            }

            const seeded: { [location: string]: SettingRow } = {};
            branches.forEach((b) => {
                seeded[b.english] = emptyRow(b.english);
            });

            if (settingResult.message === "success") {
                if (Array.isArray(settingResult.data)) {
                    settingResult.data.forEach((row: any) => {
                        if (seeded[row.location]) {
                            seeded[row.location] = {
                                ...seeded[row.location],
                                fingerprintMode: row.fingerprintMode || null,
                                fingerprintDeviceId: row.fingerprintDeviceId ? String(row.fingerprintDeviceId) : null,
                                fingerprintDeviceName: row.fingerprintDeviceName || null,
                                faceDeviceId: row.faceDeviceId ? String(row.faceDeviceId) : null,
                                faceDeviceName: row.faceDeviceName || null,
                                teacherName: row.teacherName || null,
                                lastModified: row.lastModified || null
                            };
                        }
                    });
                }
                setLegacyFaceDeviceIds(settingResult.legacyFaceDeviceIds || {});
                setIsAdmin(!!settingResult.isAdmin);
            } else {
                toast.error("지점별 설정을 불러오지 못했습니다", toastOptions);
            }

            setSettings(seeded);
            setDirty(false);
        } catch (e) {
            console.log(e);
            toast.error("불러오기 실패", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    //BioStar 재조회 (설정 행은 건드리지 않음)
    const refreshDevices = async () => {
        try {
            setRefreshing(true);
            const result: any = await getFetch(`${API}/devices?refresh=1`);
            if (result.message === "success") {
                setDevices(result.data || []);
                setFetchedAt(result.fetchedAt || Date.now());
                toast.success("장치 목록을 갱신했습니다", toastOptions);
            } else {
                toast.error(result.message === "NOT_AUTHORIZED" ? "권한이 없습니다 (todoistApiKey 필요)" : "장치 갱신 실패", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("장치 갱신 실패", toastOptions);
        } finally {
            setRefreshing(false);
        }
    };

    const deviceById = (id: any) => devices.find((d: any) => String(d.id) === String(id));

    const updateRow = (location: string, patch: Partial<SettingRow>) => {
        setSettings((prev) => ({ ...prev, [location]: { ...(prev[location] || emptyRow(location)), ...patch } }));
        setDirty(true);
    };

    const changeMode = (location: string, mode: string | null) => {
        if (mode === "usb") {
            updateRow(location, { fingerprintMode: "usb", fingerprintDeviceId: null, fingerprintDeviceName: null });
        } else if (mode === "network") {
            updateRow(location, { fingerprintMode: "network" });
        } else {
            updateRow(location, { fingerprintMode: null, fingerprintDeviceId: null, fingerprintDeviceName: null });
        }
    };

    const changeDevice = (location: string, field: "fingerprint" | "face", value: string | null) => {
        const d = value && value !== NONE ? deviceById(value) : null;
        if (field === "fingerprint") {
            updateRow(location, { fingerprintDeviceId: d ? String(d.id) : null, fingerprintDeviceName: d ? d.name : null });
        } else {
            updateRow(location, { faceDeviceId: d ? String(d.id) : null, faceDeviceName: d ? d.name : null });
        }
    };

    //전체 행 한 번에 저장
    const save = async () => {

        const rows = branches.map((b) => settings[b.english] || emptyRow(b.english));

        const invalid = rows.find((r) => r.fingerprintMode === "network" && !r.fingerprintDeviceId);
        if (invalid) {
            toast.error(`${englishLocationToKorean(invalid.location) || invalid.location}: 네트워크 방식은 기본 등록 장치를 선택해야 합니다`, toastOptions);
            return;
        }

        try {
            setSaving(true);
            const result: any = await postFetch(`${API}/settings`, {
                rows: rows.map((r) => ({
                    location: r.location,
                    fingerprintMode: r.fingerprintMode,
                    fingerprintDeviceId: r.fingerprintDeviceId,
                    fingerprintDeviceName: r.fingerprintDeviceName,
                    faceDeviceId: r.faceDeviceId,
                    faceDeviceName: r.faceDeviceName
                }))
            });

            if (result.message === "success") {
                toast.success("저장되었습니다", toastOptions);
                await load();   // teacherName / lastModified 갱신
            } else {
                toast.error(result.message === "NOT_AUTHORIZED" ? "권한이 없습니다 (todoistApiKey 필요)" : `저장에 실패했습니다 (${result.message})`, toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("저장 중 오류가 발생했습니다", toastOptions);
        } finally {
            setSaving(false);
        }
    };

    //상태 칩: USB(보라) / 온라인(초록) / 오프라인(빨강) / 장치 없음(회색) / 미설정(주황)
    const renderStatusChip = (row: SettingRow) => {
        if (row.fingerprintMode === "usb") {
            return <Chip size="sm" variant="soft" sx={{ backgroundColor: "#f3e8ff", color: "#6b21a8" }}>USB</Chip>;
        }
        if (row.fingerprintMode === "network" && row.fingerprintDeviceId) {
            const d = deviceById(row.fingerprintDeviceId);
            if (!d) {
                return <Chip size="sm" variant="soft" color="neutral">장치 없음</Chip>;
            }
            return +d.status === 1
                ? <Chip size="sm" variant="soft" color="success">온라인</Chip>
                : <Chip size="sm" variant="soft" color="danger">오프라인</Chip>;
        }
        return <Chip size="sm" variant="soft" color="warning">미설정</Chip>;
    };

    //장치 셀렉트 옵션: 그 지점 장치 먼저, 그 다음 나머지 (구분 헤더는 disabled Option)
    const renderDeviceOptions = (location: string, onlyFingerprint: boolean, currentValue: string | null) => {

        const candidates = onlyFingerprint ? devices.filter((d: any) => d.hasFingerprint) : devices;
        const mine = candidates.filter((d: any) => d.location === location);
        const others = candidates.filter((d: any) => d.location !== location);

        const item = (d: any) => (
            <Option key={String(d.id)} value={String(d.id)} sx={{ color: +d.status === 1 ? undefined : "#9ca3af" }}>
                {+d.status === 1 ? "● " : "○ "}{d.name}{d.location !== location ? ` · ${d.location ? (englishLocationToKorean(d.location) || d.location) : "미분류"}` : ""} <span style={{ color: "#9ca3af", fontSize: "11px", marginLeft: "4px" }}>#{d.id}</span>
            </Option>
        );

        const options: any[] = [<Option key={NONE} value={NONE}>선택 안 됨</Option>];

        //저장된 장치가 현재 목록에 없으면(BioStar에서 삭제 등) 값 표시용으로 항목 유지
        if (currentValue && !candidates.find((d: any) => String(d.id) === currentValue)) {
            options.push(<Option key={"missing-" + currentValue} value={currentValue} sx={{ color: "#9ca3af" }}>목록에 없는 장치 #{currentValue}</Option>);
        }

        if (mine.length > 0) {
            options.push(<Option key="__mine" value="__mine" disabled sx={{ fontSize: "11px", color: "#9ca3af", minHeight: "24px" }}>이 지점 장치</Option>);
            mine.forEach((d: any) => options.push(item(d)));
        }
        if (others.length > 0) {
            options.push(<Option key="__others" value="__others" disabled sx={{ fontSize: "11px", color: "#9ca3af", minHeight: "24px" }}>다른 지점 / 미분류</Option>);
            others.forEach((d: any) => options.push(item(d)));
        }

        return options;

    };

    const minutesAgo = fetchedAt ? Math.max(0, Math.floor((now - new Date(fetchedAt).getTime()) / 60000)) : null;

    return (
        <div style={{ width: "96%", paddingBottom: "80px" }}>

            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginTop: "50px", marginBottom: "16px" }}>
                <div>
                    <div style={{ fontFamily: "Apple_B", fontSize: "18px", marginBottom: "6px" }}>지점별 기본 장치</div>
                    <div style={{ fontFamily: "Apple_R", fontSize: "13px", color: "#6b7280" }}>
                        BioStar 장치 {devices.length}개{minutesAgo !== null ? ` · ${minutesAgo === 0 ? "방금" : `${minutesAgo}분 전`} 갱신` : ""}
                        {" · "}여기서 저장한 값이 인증 지문/얼굴 등록 모달의 기본 장치가 됩니다 (로그인한 선생님 지점 기준)
                    </div>
                </div>
                <Button size="sm" variant="outlined" color="neutral" startDecorator={<RefreshRoundedIcon />} onClick={refreshDevices} loading={refreshing} disabled={!isAdmin}>장치 새로고침</Button>
            </div>

            {loading
                ?
                <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                    <CircularProgress />
                </div>
                :
                <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "4px 20px" }}>

                    {/* 헤더 행 */}
                    <div style={{ display: "flex", alignItems: "center", padding: "10px 4px", borderBottom: "1px solid #e0e0e0", fontSize: "13px", color: "#888", gap: "12px" }}>
                        <div style={{ width: "120px", flexShrink: 0 }}>지점</div>
                        <div style={{ width: "138px", flexShrink: 0 }}>지문 등록 방식</div>
                        <div style={{ flex: 1 }}>지문 기본 등록 장치</div>
                        <div style={{ flex: 1 }}>얼굴 등록 장치</div>
                        <div style={{ width: "80px", textAlign: "center", flexShrink: 0 }}>상태</div>
                        <div style={{ width: "90px", textAlign: "right", flexShrink: 0 }}>장치 ID</div>
                    </div>

                    {branches.map((b) => {

                        const row = settings[b.english] || emptyRow(b.english);
                        const isUsb = row.fingerprintMode === "usb";
                        const legacyId = legacyFaceDeviceIds[b.english];
                        const legacyDevice = legacyId ? deviceById(legacyId) : null;

                        return (
                            <div key={b.english} style={{ display: "flex", alignItems: "center", padding: "10px 4px", borderBottom: "1px solid #f0f0f0", gap: "12px" }}>
                                <div style={{ width: "120px", flexShrink: 0 }}>
                                    <div style={{ fontFamily: "Apple_SB", fontSize: "14px" }}>{b.korean}</div>
                                    <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
                                        {row.teacherName ? `${row.teacherName} · ${formatDateTime(row.lastModified)}` : "—"}
                                    </div>
                                </div>

                                <Select
                                    size="sm"
                                    sx={{ width: "138px", flexShrink: 0 }}
                                    value={row.fingerprintMode || NONE}
                                    onChange={(e: any, v: any) => changeMode(b.english, v === NONE ? null : v)}
                                >
                                    <Option value={NONE}>선택 안 됨</Option>
                                    <Option value="network">네트워크</Option>
                                    <Option value="usb">USB 등록기</Option>
                                </Select>

                                <Select
                                    size="sm"
                                    sx={{ flex: 1, minWidth: 0 }}
                                    disabled={isUsb || row.fingerprintMode === null}
                                    placeholder={isUsb ? "— (지점 PC의 BioMini)" : "장치 선택"}
                                    value={isUsb ? null : (row.fingerprintDeviceId ? row.fingerprintDeviceId : NONE)}
                                    onChange={(e: any, v: any) => {
                                        if (v !== null && !String(v).startsWith("__")) {
                                            changeDevice(b.english, "fingerprint", v);
                                        }
                                    }}
                                >
                                    {renderDeviceOptions(b.english, true, isUsb ? null : row.fingerprintDeviceId)}
                                </Select>

                                <Select
                                    size="sm"
                                    sx={{ flex: 1, minWidth: 0 }}
                                    placeholder={legacyDevice ? `기존 기본: ${legacyDevice.name}` : (legacyId ? `기존 기본: #${legacyId}` : "장치 선택")}
                                    value={row.faceDeviceId ? row.faceDeviceId : null}
                                    onChange={(e: any, v: any) => {
                                        if (v !== null && !String(v).startsWith("__")) {
                                            changeDevice(b.english, "face", v);
                                        }
                                    }}
                                >
                                    {renderDeviceOptions(b.english, false, row.faceDeviceId)}
                                </Select>

                                <div style={{ width: "80px", textAlign: "center", flexShrink: 0 }}>{renderStatusChip(row)}</div>
                                <div style={{ width: "90px", textAlign: "right", fontSize: "12px", color: "#6b7280", flexShrink: 0 }}>
                                    {!isUsb && row.fingerprintDeviceId ? row.fingerprintDeviceId : "—"}
                                </div>
                            </div>
                        );
                    })}
                </div>
            }

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
                <div style={{ fontFamily: "Apple_R", fontSize: "12px", color: "#9ca3af" }}>
                    "등록 방식"이 USB면 지문 장치 선택이 비활성됩니다. 얼굴 등록 장치를 비워두면 기존 방식(입문/출문 장치)으로 촬영합니다.
                </div>
                <Button variant="solid" onClick={save} loading={saving} disabled={!dirty || loading || !isAdmin}>저장</Button>
            </div>

        </div>
    );
};

export default BiostarEnrollDeviceSetting;
